import React, { useEffect, useState } from "react";
import {Box, Button, Dialog, Divider, Grid2, IconButton, InputAdornment, Snackbar, TextField, Typography } from "@mui/material";

import CustomRadioButton from "../button/CustomRadioButton";
import CustomDataGrid from "../dataTable/CustomDataGrid";
import {
  AdapterDayjs,
  AddIcon,
  CloseRoundedIcon,
  CustomConfirmDialog,
  CustomToggleButton,
  DatePicker,
  dayjs,
  EditCalendarIcon,
  GridColDef,
  GridRenderCellParams,
  LocalizationProvider,
  Toaster,
  useApi,
  validationMessages
} from "../../utils/globalImports";
import { DescriptionOutlined, FlagOutlined } from "@mui/icons-material";

interface Milestone {
  id: number | null;
  subStageId: number;
  mileStoneName: string;
  description: string;
  dueDate: string;
  status: string;
}

interface MilestoneModalProps {
  open: boolean;
  onClose: () => void;
  subStageId: number;
}

const CustomMilestoneModal: React.FC<any> = ({
  open,
  onClose,
  subStageId,
}) => {
  const [formData, setFormData] = useState<Milestone>({
    id: null,
    subStageId,
    mileStoneName: "",
    description: "",
    dueDate: "",
    status: "Active",
  });
  const [initialMilestoneData, setinitialMilestoneData] = useState<Milestone[] | null>([]);

  const { execute } = useApi();

  const fetchMileStone = async () => {
    try {
      const { data } = await execute<any[]>(
        'get',
        'Proposal/SubStageMileStones',
        {
          SubStageId: subStageId
        }
      )
      const formattedData = (data || []).map(item => ({
        ...item,
        dueDate: item.dueDate
          ? dayjs(item.dueDate).format("DD-MM-YYYY")
          : "",
        status: item.status == 1 ? "Active" : "Inactive"
      }));
      setinitialMilestoneData(formattedData)
    }
    catch (error) {
      console.warn("Error", error);
    }
  }

  useEffect(() => {
    fetchMileStone();
  }, [])

  const [selectedEditId, setSelectedEditId] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<Milestone | null>(null);
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleEdit = (row: Milestone) => {
    setSelectedEditId(row.id);
    setSelectedRow(row);
    setFormData({
      id: row.id ?? null,
      subStageId: row.subStageId,
      mileStoneName: row.mileStoneName ?? "",
      description: row.description ?? "",
      dueDate: formatDate(row.dueDate),
      status: row.status,
    });

  };

  const formatDate = (value: any) => {
    if (!value) return "";
    const d = dayjs(value, "DD/MM/YYYY");
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const AlreadyExist = (name: string) => {
    return initialMilestoneData?.some(milestone =>
      milestone.mileStoneName.trim().toLowerCase() === name.trim().toLowerCase() && milestone.id !== formData.id);
  }

  const handleSubmit = async (isStatusUpdate: boolean = false) => {
    try {
      if (formData.mileStoneName.length <= 0 || formData.description.length <= 0 || !formData.dueDate) {
        setToast({
          open: true,
          message: "Name , Due date and Description is required",
          type: "error"
        });
        return;
      }
      if (AlreadyExist(formData.mileStoneName)) {
        setToast({
          open: true,
          message: "A milestone with this name already exists",
          type: "error"
        });
        return;
      }
      const payLoad = {
        id: formData.id ?? null,
        subStageID: subStageId,
        mileStoneName: formData.mileStoneName,
        dueDate: formData.dueDate,
        description: formData.description,
        //status: isStatusUpdate ? (formData.status === "Active" ? 0 : 1) : (formData.status === "Active" ? 1 : 0)
        status: isStatusUpdate
          ? Number(formData.status !== "Active")
          : Number(formData.status === "Active")
      };

      const { data } = await execute<boolean>(
        "post",
        "Proposal/SubStageMileStones",
        payLoad
      );

      if (data === true) {
        setToast({
          open: true,
          message:
            formData.id == null
              ? "Added Successfully"
              : "Updated Successfully",
          type: "success"
        });
        setConfirmOpen(false);
        handleCancel();
        fetchMileStone();
      } else {
        setToast({
          open: true,
          message: "Operation failed every field is required",
          type: "error"
        });
      }

    } catch (error) {
      console.warn("Error", error);
    }
  };
  const handleCancel = () => {
    setSelectedEditId(null);
    setSelectedRow(null);
    setFormData({
      id: null,
      subStageId: subStageId,
      mileStoneName: "",
      description: "",
      dueDate: "",
      status: "Active",
    });

  }
  const colors = [
    { subColor: "#eef2ff", color: "#3949ab" },
    { subColor: "#e6f4ea", color: "#2e7d32" },
    { subColor: "#e3f2fd", color: "#1565c0" },
    { subColor: "#fff3e0", color: "#ef6c00" },
    { subColor: "#ede7f6", color: "#5e35b1" },
    { subColor: "#fce4ec", color: "#c2185b" },
    { subColor: "#f7dbdb", color: "#e70b12" },
  ];
  const milestoneColumns: GridColDef[] = [
    { field: "mileStoneName", headerName: "Name", flex: 1,headerAlign:"center",
      renderCell: (params: GridRenderCellParams<Milestone>) => {
              const color = colors[Math.floor(Math.random() * 7) + 0];
              return (
                <>
      
                  <Box sx={{
                    height: "29px", width: "29px", mr: 1, borderRadius: "50%",
                    backgroundColor: color.subColor,
                    color: color.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight:"600",
                  }}>
                    {params.value.substring(0, 1).toUpperCase()}
                  </Box>
                  {params.value}</>
              );
            }
     },
    { field: "description", headerName: "Description", flex: 1.5,headerAlign:"center" },
    { field: "dueDate", headerName: "Due Date", flex: 1,headerAlign:"center" },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Milestone>) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row)}
          >
            <EditCalendarIcon sx={{ color: "#a3c63d" }} />
          </IconButton>
          <CustomToggleButton
            isOn={params.row.status === "Active"}
            onClick={() => { handleEdit(params.row); setConfirmOpen(true); }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", px: 3, pt: 3, pb: 2 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: "50%",
            backgroundColor: "#e8f5e9",
            display: "flex", alignItems: "center", justifyContent: "center",
            mr: 1.5, flexShrink: 0,
          }}
        >
          <FlagOutlined sx={{ color: "#7cb342", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a" }}>
            Manage Milestones
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#888", mt: 0.3 }}>
            Add and manage project milestones with due dates and descriptions.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ ml: "auto", color: "#aaa", "&:hover": { color: "#555" } }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* ── Form ── */}
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Grid2 container spacing={2}>

          {/* Milestone Name */}
          <Grid2 size={6}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Milestone Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter milestone name"
              name="mileStoneName"
              value={formData.mileStoneName}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FlagOutlined sx={{ fontSize: 18, color: "rgba(150, 193, 33, 0.75)" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "8px" },
              }}
            />
          </Grid2>

          {/* Due Date */}
          <Grid2 size={6}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Due Date <span style={{ color: "red" }}>*</span>
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                value={formData.dueDate ? dayjs(formData.dueDate) : null}
                onChange={(newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    dueDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid2>

          {/* Description */}
          <Grid2 size={12}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Description <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={1}
              placeholder="Enter milestone description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                    <DescriptionOutlined sx={{ fontSize: 18, color: "#aaa" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "8px" ,},
              }}
            />
          </Grid2>

          {/* Status Radio (edit mode only) */}
          {(formData.id != null && formData.id > 0) && (
            <Grid2 size={12} display="flex">
              <CustomRadioButton
                label="Active" value="Active" name="status"
                checked={formData.status === "Active"} onChange={handleChange}
              />
              <CustomRadioButton
                label="Inactive" value="Inactive" name="status"
                checked={formData.status === "Inactive"} onChange={handleChange}
              />
            </Grid2>
          )}

          {/* Buttons */}
          <Grid2 size={12} display="flex" gap={1} mt={0.5}>
            <Button
              variant="contained"
              onClick={() => {handleSubmit()}}
              startIcon={selectedEditId ? null : <AddIcon />}
              sx={{
                backgroundColor: "#8bc34a",
                "&:hover": { backgroundColor: "#7cb342" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
              }}
            >
              {selectedEditId ? "Update" : "Add Milestone"}
            </Button>

            {selectedEditId && (
              <Button
                variant="contained"
                onClick={handleCancel}
                sx={{
                  backgroundColor: "#1e66ab",
                  "&:hover": { backgroundColor: "rgba(150,193,33,0.75)" },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                }}
              >
                Cancel
              </Button>
            )}
          </Grid2>

        </Grid2>
      </Box>
      <Divider />

      {/* ── Milestone List Header ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1a1a1a" }}>
          Milestone List
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2 }}>
        <CustomDataGrid rows={initialMilestoneData} columns={milestoneColumns} />
      </Box>

      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error"}
        onClose={() => setToast({ ...toast, open: false })}
      />
      <CustomConfirmDialog
        open={confirmOpen}
        title={formData.status === "Active" ? "Deactivate Milestone" : "Activate Milestone"}
        message={validationMessages.SubFeeStage_Confirm(
          formData.status === "Active" ? "Inactive" : "Active"
        )}
        onConfirm={() => handleSubmit(true)}
        onCancel={() => setConfirmOpen(false)}
        onClose={() => setConfirmOpen(false)}
      />
    </Dialog>   
  );
};

export default CustomMilestoneModal;
